import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SecurityContext,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { TemplatesService } from '@vendasta/templates';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, take, tap } from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import moment from 'moment';
import Autolinker from 'autolinker';
import { HttpErrorResponse } from '@angular/common/http';
import { HydrationOptions } from '@vendasta/templates/lib/_internal';

@Component({
  selector: 'app-loop-post-type-templates-settings',
  styleUrls: ['./post-type-templates-settings.component.scss'],
  templateUrl: './post-type-templates-settings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PostTypeTemplatesSettingsComponent implements OnInit {
  @Input() parentForm: UntypedFormGroup;
  @Input() templateId: string;
  @Output() loaded: EventEmitter<boolean> = new EventEmitter();
  fullHTML$$: ReplaySubject<string> = new ReplaySubject<string>(1);
  fullHTML$: Observable<string> = this.fullHTML$$.asObservable();
  previewFullError$$: ReplaySubject<string> = new ReplaySubject<string>(1);
  previewFullError$: Observable<string> = this.previewFullError$$.asObservable();
  previewFullHTML$: Observable<SafeHtml>;
  styles$$: ReplaySubject<string> = new ReplaySubject<string>(1);
  styles$: Observable<string> = this.styles$$.asObservable();
  previewStylesError$$: ReplaySubject<string> = new ReplaySubject<string>(1);
  previewStylesError$: Observable<string> = this.previewStylesError$$.asObservable();
  previewStyles$: Observable<SafeHtml>;
  compactHTML$$: ReplaySubject<string> = new ReplaySubject<string>(1);
  compactHTML$: Observable<string> = this.compactHTML$$.asObservable();
  previewCompactError$$: ReplaySubject<string> = new ReplaySubject<string>(1);
  previewCompactError$: Observable<string> = this.previewCompactError$$.asObservable();
  previewCompactHTML$: Observable<SafeHtml>;
  sampleData$$: ReplaySubject<string> = new ReplaySubject<string>(1);
  sampleData$: Observable<string> = this.sampleData$$.asObservable();

  exampleDate = new Date();

  constructor(private templatesService: TemplatesService, private readonly sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    // TODO: Hydration options

    if (this.templateId) {
      const templateIds = [this.templateId, this.templateId + '-styles', this.templateId + '-compact'];
      this.templatesService
        .getMulti('the-loop', templateIds)
        .pipe(
          take(1),
          tap((templates) => {
            // TODO handle templates[].error
            this.fullHTML$$.next(templates[0].template ? templates[0].template.content : '');
            this.styles$$.next(templates[1].template ? templates[1].template.content : '');
            this.compactHTML$$.next(templates[2].template ? templates[2].template.content : '');
            if (templates[0].template.hydrationOptions) {
              this.setInitialHydrationOptions(templates[0].template.hydrationOptions);
            }
          }),
          tap(() => this.loaded.emit(true)),
        )
        .subscribe();
    } else {
      this.fullHTML$$.next('');
      this.styles$$.next('');
      this.compactHTML$$.next('');
      this.loaded.emit(true);
    }
    // TODO: Add sample data to Loop Post Type model so that this persists
    this.sampleData$$.next('{}');

    this.previewStyles$ = combineLatest([this.styles$, this.sampleData$]).pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(([styles, sampleData]) => {
        return this.templatesService.render(styles, convertsampleDataToJson(sampleData)).pipe(
          tap(() => this.previewStylesError$$.next('')),
          catchError((err: HttpErrorResponse) => {
            this.previewStylesError$$.next(err.error.message);
            return of('');
          }),
        );
      }),
      map((renderedTemplate) => {
        return this.sanitizer.bypassSecurityTrustHtml(renderedTemplate);
      }),
    );

    this.previewFullHTML$ = combineLatest([this.fullHTML$, this.sampleData$]).pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(([fullHTML, sampleData]) =>
        this.templatesService.render(fullHTML, convertsampleDataToJson(sampleData)).pipe(
          tap(() => this.previewFullError$$.next('')),
          catchError((err: HttpErrorResponse) => {
            this.previewFullError$$.next(err.error.message);
            return of('');
          }),
        ),
      ),
      map((renderedTemplate) => {
        return this.sanitizer.sanitize(SecurityContext.HTML, Autolinker.link(renderedTemplate));
      }),
    );

    this.previewCompactHTML$ = combineLatest([this.compactHTML$, this.sampleData$]).pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(([compactHTML, sampleData]) =>
        this.templatesService.render(compactHTML, convertsampleDataToJson(sampleData)).pipe(
          tap(() => this.previewCompactError$$.next('')),
          catchError((err: HttpErrorResponse) => {
            this.previewCompactError$$.next(err.error.message);
            return of('');
          }),
        ),
      ),
      map((renderedTemplate) => {
        return this.sanitizer.sanitize(SecurityContext.HTML, Autolinker.link(renderedTemplate));
      }),
    );
  }

  updateFullHTML(content: string): void {
    this.fullHTML$$.next(content);
    this.parentForm.get('templates').patchValue({
      fullHtml: content,
    });
  }

  updateStyles(content: string): void {
    this.styles$$.next(content);
    this.parentForm.get('templates').patchValue({
      styles: content,
    });
  }

  updateCompactHTML(content: string): void {
    this.compactHTML$$.next(content);
    this.parentForm.get('templates').patchValue({
      compactHTML: content,
    });
  }

  updateSampleData(content: string): void {
    this.sampleData$$.next(content);
  }

  fromNow(date: Date): string {
    return moment(date).fromNow();
  }

  setInitialHydrationOptions(hydrationOptions: HydrationOptions): void {
    const hydrators = this.parentForm.get('templates').get('hydrators');
    if (hydrationOptions.accountGroups) {
      const accountGroups = hydrators.get('accountGroups') as UntypedFormGroup;
      accountGroups.get('checked').setValue(true);
      this.hydratorChecked('accountGroups', true);
      accountGroups.get('params').get('accountGroupIds').setValue(hydrationOptions.accountGroups.accountGroupIds);
      accountGroups
        .get('params')
        .get('inflatedPropertyName')
        .setValue(hydrationOptions.accountGroups.inflatedPropertyName);
    }
    if (hydrationOptions.apps) {
      const apps = hydrators.get('apps') as UntypedFormGroup;
      apps.get('checked').setValue(true);
      this.hydratorChecked('apps', true);
      apps.get('params').get('appKeys').setValue(hydrationOptions.apps.appKeys);
      apps.get('params').get('partnerId').setValue(hydrationOptions.apps.partnerId);
      apps.get('params').get('marketId').setValue(hydrationOptions.apps.marketId);
      apps.get('params').get('inflatedPropertyName').setValue(hydrationOptions.apps.inflatedPropertyName);
    }
    if (hydrationOptions.packages) {
      const packages = hydrators.get('packages') as UntypedFormGroup;
      packages.get('checked').setValue(true);
      this.hydratorChecked('packages', true);
      packages.get('params').get('packageIds').setValue(hydrationOptions.packages.packageIds);
      packages.get('params').get('inflatedPropertyName').setValue(hydrationOptions.packages.inflatedPropertyName);
    }
    if (hydrationOptions.branding) {
      const branding = hydrators.get('branding') as UntypedFormGroup;
      branding.get('checked').setValue(true);
      this.hydratorChecked('branding', true);
      branding.get('params').get('partnerId').setValue(hydrationOptions.branding.partnerId);
      branding.get('params').get('marketId').setValue(hydrationOptions.branding.marketId);
      branding.get('params').get('inflatedPropertyName').setValue(hydrationOptions.branding.inflatedPropertyName);
    }
    if (hydrationOptions.users) {
      const users = hydrators.get('users') as UntypedFormGroup;
      users.get('checked').setValue(true);
      this.hydratorChecked('users', true);
      users.get('params').get('userIds').setValue(hydrationOptions.users.userIds);
      users.get('params').get('inflatedPropertyName').setValue(hydrationOptions.users.inflatedPropertyName);
    }
    if (hydrationOptions.salesActivities) {
      const salesActivities = hydrators.get('salesActivities') as UntypedFormGroup;
      salesActivities.get('checked').setValue(true);
      this.hydratorChecked('salesActivities', true);
      salesActivities
        .get('params')
        .get('salesActivityKeys')
        .setValue(hydrationOptions.salesActivities.salesActivityKeys);
      salesActivities
        .get('params')
        .get('inflatedPropertyName')
        .setValue(hydrationOptions.salesActivities.inflatedPropertyName);
    }
    if (hydrationOptions.partnerConfig) {
      const partnerConfig = hydrators.get('partnerConfig') as UntypedFormGroup;
      partnerConfig.get('checked').setValue(true);
      this.hydratorChecked('partnerConfig', true);
      partnerConfig.get('params').get('partnerId').setValue(hydrationOptions.partnerConfig.partnerId);
      partnerConfig.get('params').get('marketId').setValue(hydrationOptions.partnerConfig.marketId);
      partnerConfig
        .get('params')
        .get('inflatedPropertyName')
        .setValue(hydrationOptions.partnerConfig.inflatedPropertyName);
    }
    if (hydrationOptions.task) {
      const task = hydrators.get('task') as UntypedFormGroup;
      task.get('checked').setValue(true);
      this.hydratorChecked('task', true);
      task.get('params').get('taskKeys').setValue(hydrationOptions.task.taskKeys);
      task.get('params').get('inflatedPropertyName').setValue(hydrationOptions.task.inflatedPropertyName);
    }
  }

  hydratorChecked(hydratorName: string, checked: boolean): void {
    const params = this.parentForm
      .get('templates')
      .get('hydrators')
      .get(hydratorName)
      .get('params') as UntypedFormGroup;
    Object.keys(params.controls).forEach((field) => {
      const c = params.controls[field];
      if (checked) {
        c.enable();
      } else {
        c.disable();
      }
    });
  }
}

function convertsampleDataToJson(sampleData: string): any {
  let sampleJson = {};
  try {
    sampleJson = JSON.parse(sampleData || '{}');
  } catch {
    // do nothing
  }
  return sampleJson;
}
